import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Layout from '../components/layout/Layout';
import { useAuth } from '../hooks/useAuth';
import { Paydle } from '../types/paydle';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { firestore } from '../services/firebase';

const MyPaydles: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [paydles, setPaydles] = useState<Paydle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Load user's paydles
  useEffect(() => {
    const loadPaydles = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const paydlesRef = collection(firestore, 'paydles');

        let paydlesData: Paydle[] = [];

        // Try with orderBy first (requires index)
        try {
          const q = query(
            paydlesRef,
            where('creatorId', '==', currentUser.uid),
            orderBy('createdAt', 'desc')
          );

          const querySnapshot = await getDocs(q);
          querySnapshot.forEach((doc) => {
            paydlesData.push({
              id: doc.id,
              ...doc.data(),
            } as Paydle);
          });
        } catch (indexError: any) {
          // Fallback if index is still building
          if (indexError?.message?.includes('index')) {
            console.log('Index still building, using fallback query...');
            const q = query(
              paydlesRef,
              where('creatorId', '==', currentUser.uid)
            );

            const querySnapshot = await getDocs(q);
            querySnapshot.forEach((doc) => {
              paydlesData.push({
                id: doc.id,
                ...doc.data(),
              } as Paydle);
            });

            // Sort manually on client side
            paydlesData.sort((a, b) => {
              const aTime = (a.createdAt as any)?.toDate?.() || new Date(a.createdAt as any);
              const bTime = (b.createdAt as any)?.toDate?.() || new Date(b.createdAt as any);
              return bTime.getTime() - aTime.getTime();
            });
          } else {
            throw indexError;
          }
        }

        setPaydles(paydlesData);
      } catch (err: any) {
        console.error('Error loading paydles:', err);
        setError('Failed to load your paydles');
      } finally {
        setLoading(false);
      }
    };

    loadPaydles();
  }, [currentUser]);

  // Calculate win rate
  const getWinRate = (paydle: Paydle) => {
    if (paydle.totalAttempts === 0) return 0;
    return Math.round((paydle.totalWins / paydle.totalAttempts) * 100);
  };

  // Format date
  const formatDate = (date: any) => {
    if (!date) return 'Unknown';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (!currentUser) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Sign In Required</h2>
            <p className="text-gray-600 mb-6">
              You need to be signed in to view your paydles.
            </p>
            <Link
              to="/sign-in"
              className="inline-block px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Sign In
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">My Paydles</h1>
            <p className="text-gray-600">Manage your word challenges and track player attempts</p>
          </div>
          <div className="flex gap-3 mt-4 md:mt-0">
            <Link
              to="/create-paydle"
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Create Paydle
            </Link>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-green-600"></div>
          </div>
        )}

        {/* Empty State */}
        {!loading && paydles.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg"
          >
            <div className="text-6xl mb-4">🔤</div>
            <h3 className="text-2xl font-bold mb-2 text-gray-800">No Paydles Yet</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Create your first word puzzle challenge and share it with others!
            </p>
            <Link
              to="/create-paydle"
              className="inline-block px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Create Your First Paydle
            </Link>
          </motion.div>
        )}

        {/* Paydles Grid */}
        {!loading && paydles.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {paydles.map((paydle, index) => (
              <motion.div
                key={paydle.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => navigate(`/paydle/manage/${paydle.accessToken}`)}
                className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all cursor-pointer border-2 border-gray-200 hover:border-green-400 overflow-hidden"
              >
                {/* Header */}
                <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-4">
                  <div className="flex items-center justify-between">
                    <span
                      className={`px-3 py-1 text-xs font-semibold rounded-full ${
                        paydle.isActive
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-500 text-white'
                      }`}
                    >
                      {paydle.isActive ? 'Active' : 'Inactive'}
                    </span>
                    <span className="text-white text-2xl font-bold tracking-widest">
                      {paydle.word}
                    </span>
                  </div>
                </div>

                {/* Stats */}
                <div className="p-4">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-green-600">
                        ${paydle.price.toFixed(2)}
                      </p>
                      <p className="text-xs text-gray-500">Prize per Loss</p>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl font-bold text-purple-600">
                        {paydle.totalAttempts}
                      </p>
                      <p className="text-xs text-gray-500">Total Plays</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div className="text-center bg-green-50 rounded p-2">
                      <p className="text-lg font-bold text-green-600">{paydle.totalWins}</p>
                      <p className="text-xs text-gray-600">Wins</p>
                    </div>
                    <div className="text-center bg-red-50 rounded p-2">
                      <p className="text-lg font-bold text-red-600">{paydle.totalLosses}</p>
                      <p className="text-xs text-gray-600">Losses</p>
                    </div>
                    <div className="text-center bg-blue-50 rounded p-2">
                      <p className="text-lg font-bold text-blue-600">{getWinRate(paydle)}%</p>
                      <p className="text-xs text-gray-600">Win Rate</p>
                    </div>
                  </div>

                  {/* Share Info */}
                  <div className="border-t pt-3 flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500">Created</p>
                      <p className="text-sm font-medium text-gray-700">
                        {formatDate(paydle.createdAt)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const shareUrl = `${window.location.origin}/paydle/play/${paydle.shareId}`;
                          navigator.clipboard.writeText(shareUrl);
                          alert('Share link copied!');
                        }}
                        className="px-3 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition-colors"
                      >
                        📋 Copy Link
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MyPaydles;
